import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ProductDetails } from '@web-solutions/core/store/billing/selectors';

import { PaymentSystem } from '../../../constants';

import type { SdkInstance, CardPaymentResponse } from '../../truegate-sdk';
import { setPending } from '../../../../core/store/billing/actions';

import { CardForm } from './form';
import getLocale from '../../../../core/localization/get-locale';
import { useStore } from '../../../../../apps/virtual-brainary/src/onboarding/store/store';
import { buildAnonEmail } from '../../../../../apps/virtual-brainary/src/onboarding/helpers/anonEmail';

interface CardElementProps {
  isDarkMode?: boolean;
  orderDetails: any;
  formStyles?: React.CSSProperties;
  options: any;
  buttonTitle?: string;
  product: ProductDetails;
  onReady?: (e: any) => void;
  onSubmit?: (e: any) => void;
  onUserInfoChange?: (e: any) => void;
  onSuccess?: (e: any) => void;
  onError?: (e: any) => void;
}

export const CardElement: React.FC<CardElementProps> = ({
  isDarkMode,
  options,
  buttonTitle,
  product,
  onReady,
  onSubmit,
  onUserInfoChange,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch();
  const productId = product?.truegateId || product?.productCode || 'no product';

  const intent =
    options?.orderDetails?.products?.[productId] ||
    options?.orderDetails?.one_time_products?.[productId] ||
    options?.orderDetails;

  const [isLoading, setIsLoading] = useState(false);
  const [cardHolderName, setCardHolderName] = useState('');
  const [isCardNumberValid, setIsCardNumberValid] = useState(true);
  const [isExpirationValid, setIsExpirationValid] = useState(true);
  const [isSecurityCodeValid, setIsSecurityCodeValid] = useState(true);
  const [isCardHolderNameValid, setIsCardHolderNameValid] = useState(true);
  const cardPaymentRef = useRef<CardPaymentResponse | null>(null);
  const sdkInstanceRef = useRef<SdkInstance | null>(null);
  const intentIdRef = useRef<string | null>(null);
  const cardHolderNameInputRef = useRef<HTMLInputElement | null>(null);
  const realEmail = useStore((state) => state.email);
  const userId = useStore((state) => state.userId);
  // Deferred-email flow: the user pays before giving an email, so fall back to the
  // synthetic address the truegate customer was created with — otherwise the form
  // below bails out and nothing renders.
  const email = realEmail || (userId ? buildAnonEmail(userId) : undefined);
  const submittedRef = useRef(false);
  // Dedupes error reporting per submit attempt: the SDK both emits
  // PAYMENT_ERROR and throws from submit(), which used to fire onError (and
  // the intent re-creation behind it) twice.
  const errorReportedRef = useRef(false);

  // Truegate transactions are single-use; a stale instance keeps its window
  // listeners and reacts to events of the next one, so it must be destroyed
  // before a new instance is initialized for a fresh transaction.
  const destroySdkInstance = () => {
    try {
      sdkInstanceRef.current?.destroy?.();
    } catch (error) {
      console.warn('Truegate SDK destroy failed', error);
    }
    sdkInstanceRef.current = null;
    cardPaymentRef.current = null;
  };

  const formFieldsValidation = useRef<{
    [key: string]: {
      isValid: boolean;
    };
  }>({
    cardNumber: { isValid: false },
    expiration: { isValid: false },
    securityCode: { isValid: false },
  });

  useEffect(() => {
    const currentIntentId = intent?.transactionId;
    if (intentIdRef.current === currentIntentId && sdkInstanceRef.current) {
      console.log(
        'Card payment already initialized for this intent, skipping...'
      );
      return;
    }

    // Kill the previous instance before initializing a new one — Truegate
    // requires a fresh transactionId AND a fresh SDK instance after a failed
    // transaction; a stale instance with the same event id would keep
    // receiving the new instance's events.
    destroySdkInstance();

    setCardHolderName('');
    formFieldsValidation.current.cardNumber.isValid = true;
    formFieldsValidation.current.expiration.isValid = true;
    formFieldsValidation.current.securityCode.isValid = true;
    errorReportedRef.current = false;
    intentIdRef.current = currentIntentId;

    const init = async () => {
      // Unique per transaction: the id routes postMessage events between the
      // SDK and its iframes, so it must never be shared between instances.
      const ID = `CARD_PAYMENT_TRUEGATE_${intent.transactionId}`;

      //@ts-ignore
      const sdkInstance: SdkInstance = new window.truegateSdk({
        id: ID,
        transactionId: intent.transactionId,
        env: process.env.REACT_APP_TRUEGATE_ENV as any,
      });
      sdkInstanceRef.current = sdkInstance;

      sdkInstance.on('CARD_PAYMENT_READY' as any, () => {
        onReady?.({
          paymentSystem: PaymentSystem.TRUEGATE,
        });
      });

      sdkInstance.on('CARD_NUMBER_INPUT' as any, () => {
        if (!submittedRef.current) {
          formFieldsValidation.current.cardNumber.isValid = true;
        }
      });

      sdkInstance.on('CARD_NUMBER_BLUR' as any, () => {
        if (submittedRef.current) {
          console.log(
            'CARD_NUMBER_BLUR',
            formFieldsValidation.current.cardNumber.isValid
          );
          setIsCardNumberValid(formFieldsValidation.current.cardNumber.isValid);
        }
      });

      sdkInstance.on('CARD_NUMBER_VALIDATION_RESULT' as any, (payload: any) => {
        formFieldsValidation.current.cardNumber.isValid =
          payload.details.isValid;
      });

      sdkInstance.on('CARD_EXPIRATION_INPUT' as any, () => {
        if (!submittedRef.current) {
          formFieldsValidation.current.expiration.isValid = true;
        }
      });

      sdkInstance.on('CARD_EXPIRATION_BLUR' as any, () => {
        if (submittedRef.current) {
          setIsExpirationValid(formFieldsValidation.current.expiration.isValid);
        }
      });
      sdkInstance.on(
        'CARD_EXPIRATION_VALIDATION_RESULT' as any,
        (payload: any) => {
          formFieldsValidation.current.expiration.isValid =
            payload.details.isValid;
        }
      );

      sdkInstance.on('CARD_SECURITY_CODE_BLUR' as any, () => {
        if (submittedRef.current) {
          setIsSecurityCodeValid(
            formFieldsValidation.current.securityCode.isValid
          );
        }
      });

      sdkInstance.on('CARD_SECURITY_CODE_INPUT' as any, () => {
        if (!submittedRef.current) {
          formFieldsValidation.current.securityCode.isValid = true;
        }
      });

      sdkInstance.on(
        'CARD_SECURITY_CODE_VALIDATION_RESULT' as any,
        (payload: any) => {
          formFieldsValidation.current.securityCode.isValid =
            payload.details.isValid;
        }
      );

      sdkInstance.on('PAYMENT_ERROR' as any, (payload: any) => {
        console.log('PAYMENT_ERROR', payload);
        setIsLoading(false);
        dispatch(setPending(false));
        if (errorReportedRef.current) {
          return;
        }
        errorReportedRef.current = true;
        onError?.({
          method: 'card',
          price_id: productId,
          paymentSystem: PaymentSystem.TRUEGATE,
          ...payload,
        });
      });

      sdkInstance.on('PAYMENT_STATUS' as any, (payload: any) => {
        if (
          payload.channel === 'CARD_PAYMENT' &&
          payload.details.status === 'FAILED'
        ) {
          setIsLoading(false);
          dispatch(setPending(false));
          if (errorReportedRef.current) {
            return;
          }
          errorReportedRef.current = true;
          onError?.({
            method: 'card',
            price_id: productId,
            paymentSystem: PaymentSystem.TRUEGATE,
            ...payload,
          });
        }
        if (
          payload.details.status === 'PENDING' &&
          payload.channel === 'CARD_PAYMENT'
        ) {
          dispatch(setPending(true));
        }
        if (
          payload.details.status === 'SUCCESS' &&
          payload.channel === 'CARD_PAYMENT'
        ) {
          setIsLoading(false);
          dispatch(setPending(false));
          onSuccess?.({
            amount: product.amount,
            currency: product.currency,
            email: email,
            customer_email: email,
            subscription_id: productId,
            transaction_id: intent.transactionId,
            paymentSystem: PaymentSystem.TRUEGATE,
            method: 'card',
            price_id: productId,
          });
        }
      });

      sdkInstance.on('CARD_PAYMENT_CARD_DETAILS' as any, (payload: any) => {
        onUserInfoChange?.({
          card_bank: payload.details?.bank,
          card_brand: payload.details?.provider,
          card_last4: payload.details?.last4,
        });
      });

      sdkInstance.on('CARD_SECURITY_CODE_COMPLETE' as any, (payload: any) => {
        if (payload.details.isCompleted) {
          cardHolderNameInputRef.current?.focus();
        }
      });

      try {
        await sdkInstance.init();

        const cardPayment = await sdkInstance.initCardPayment({
          cardNumberId: `card_number_id_${intent?.transactionId}`,
          expirationId: `expiration_id_${intent?.transactionId}`,
          securityCodeId: `security_code_id_${intent?.transactionId}`,
          options: {
            PLACEHOLDER_CARD_NUMBER: '1234 1234 1234 1234',
            PLACEHOLDER_EXPIRATION: 'MM / YY',
            PLACEHOLDER_SECURITY_CODE: 'CVV',
            FONT_NAME: 'OPEN SANS' as any,
            FONT_SIZE: '16px',
            LINE_HEIGHT: '24px',
            FONT_WEIGHT: '400',
            COLOR: '#000000',
          },
        });

        // A newer intent may have superseded this init while it was awaiting —
        // in that case this instance is already destroyed, don't resurrect it.
        if (sdkInstanceRef.current !== sdkInstance) {
          return;
        }

        cardPaymentRef.current = cardPayment;
      } catch (error) {
        if (sdkInstanceRef.current === sdkInstance) {
          console.error('Truegate SDK init failed', error);
        }
        // Aborted init of a superseded/destroyed instance — ignore.
      }
    };

    if (intent && currentIntentId) {
      init();
    }

    return () => {
      destroySdkInstance();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent?.transactionId]);

  const checkValidation = () => {
    const isCardHolderValid = !!cardHolderName.trim();
    const isCardNumberValid = formFieldsValidation.current.cardNumber.isValid;
    const isExpirationValid = formFieldsValidation.current.expiration.isValid;
    const isSecurityCodeValid = formFieldsValidation.current.securityCode.isValid;

    setIsCardHolderNameValid(isCardHolderValid);
    setIsCardNumberValid(isCardNumberValid);
    setIsExpirationValid(isExpirationValid);
    setIsSecurityCodeValid(isSecurityCodeValid);

    return isCardHolderValid && isCardNumberValid && isExpirationValid && isSecurityCodeValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    submittedRef.current = true;

    if (!checkValidation()) {
      return;
    }

    if (!cardPaymentRef.current) {
      return;
    }

    errorReportedRef.current = false;

    try {
      setIsLoading(true);
      dispatch(setPending(true));
      await onSubmit?.({
        amount: product.amount,
        currency: product.currency,
        email: email,
        customer_email: email,
        subscription_id: productId,
        transaction_id: intent.transactionId,
        paymentSystem: PaymentSystem.TRUEGATE,
        method: 'card',
        price_id: productId,
      });
      await cardPaymentRef.current.submit({
        cardHolderName: cardHolderName.trim(),
      });
    } catch (error: any) {
      console.error('Card payment submit error:', error);
      // The SDK emits PAYMENT_ERROR before throwing from submit() — report
      // only if the event handler hasn't already done it, otherwise the intent
      // would be re-created twice.
      if (!errorReportedRef.current) {
        errorReportedRef.current = true;
        onError?.({
          paymentSystem: PaymentSystem.TRUEGATE,
          price_id: productId,
          method: 'card',
          ...error,
        });
      }
      setIsLoading(false);
      dispatch(setPending(false));
    }
  };

  const handleCardHolderNameBlur = (e: any) => {
    if (submittedRef.current) {
      setIsCardHolderNameValid(!!cardHolderName.trim());
    }
  };

  const handleCardHolderNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCardHolderName(e.target.value);
  };

  if (!intent || !email) {
    return null;
  }

  const isDisabled = submittedRef.current
    ? isLoading ||
      !(
        cardHolderName.trim() &&
        isCardNumberValid &&
        isExpirationValid &&
        isSecurityCodeValid
      )
    : isLoading;

  const locale = getLocale();

  return (
    <CardForm
      isDarkMode={isDarkMode}
      id={intent?.transactionId}
      isDisabled={isDisabled}
      isCardNumberValid={isCardNumberValid}
      isExpirationValid={isExpirationValid}
      isSecurityCodeValid={isSecurityCodeValid}
      cardHolderName={cardHolderName}
      isCardHolderNameValid={isCardHolderNameValid}
      cardHolderNameInputRef={cardHolderNameInputRef}
      buttonTitle={buttonTitle}
      onSubmit={handleSubmit}
      handleCardHolderNameBlur={handleCardHolderNameBlur}
      handleCardHolderNameChange={handleCardHolderNameChange}
      loading={isLoading}
    />
  );
};
